import { useRef } from "react";
import {
	type ResolvedSeo,
	resolveSeo,
	SEO_DEFAULT_ROBOTS,
	type SeoProps,
} from "../seo/metadata";

function upsertMeta(
	attribute: "name" | "property",
	key: string,
	content: string,
) {
	let element = document.head.querySelector<HTMLMetaElement>(
		`meta[${attribute}="${key}"]`,
	);
	if (!element) {
		element = document.createElement("meta");
		element.setAttribute(attribute, key);
		document.head.append(element);
	}
	element.setAttribute("content", content);
}

function upsertCanonical(href: string) {
	let link = document.head.querySelector<HTMLLinkElement>(
		'link[rel="canonical"]',
	);
	if (!link) {
		link = document.createElement("link");
		link.setAttribute("rel", "canonical");
		document.head.append(link);
	}
	link.setAttribute("href", href);
}

function applySeoToDocument(seo: ResolvedSeo) {
	document.title = seo.title;
	upsertCanonical(seo.url);

	upsertMeta("name", "description", seo.description);
	upsertMeta("name", "robots", seo.robots);

	upsertMeta("property", "og:type", seo.ogType);
	upsertMeta("property", "og:site_name", seo.siteName);
	upsertMeta("property", "og:title", seo.title);
	upsertMeta("property", "og:description", seo.description);
	upsertMeta("property", "og:url", seo.url);
	upsertMeta("property", "og:image", seo.image);

	upsertMeta("name", "twitter:card", "summary");
	upsertMeta("name", "twitter:title", seo.title);
	upsertMeta("name", "twitter:description", seo.description);
	upsertMeta("name", "twitter:image", seo.image);
}

function resolvedSeoMatches(left: ResolvedSeo, right: ResolvedSeo) {
	return (
		left.title === right.title &&
		left.description === right.description &&
		left.robots === right.robots &&
		left.ogType === right.ogType &&
		left.url === right.url &&
		left.image === right.image &&
		left.siteName === right.siteName
	);
}

export default function Seo({
	title,
	description,
	path,
	robots = SEO_DEFAULT_ROBOTS,
	ogType = "website",
}: SeoProps) {
	const seo = resolveSeo({
		title,
		description,
		path,
		robots,
		ogType,
	});
	const lastAppliedSeoRef = useRef<ResolvedSeo | undefined>(undefined);

	// Render-time ref guards head writes.
	if (
		typeof document !== "undefined" &&
		(!lastAppliedSeoRef.current ||
			!resolvedSeoMatches(lastAppliedSeoRef.current, seo))
	) {
		applySeoToDocument(seo);
		lastAppliedSeoRef.current = seo;
	}

	return null;
}
